import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { CATEGORY_META } from "@/i18n/translations";
import ListingCard from "@/components/ListingCard";
import SearchBar from "@/components/SearchBar";
import { useSeo } from "@/lib/seo";

export default function CategoryPage() {
  const { category } = useParams();
  const { lang, t } = useLang();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [intent, setIntent] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = { category, limit: 60 };
    if (intent) params.intent = intent;
    api.get("/listings", { params }).then(({ data }) => setListings(data)).catch(() => {}).finally(() => setLoading(false));
  }, [category, intent]);

  const meta = CATEGORY_META[category];
  const title = meta ? meta[lang] : category;

  useSeo({
    title,
    description: lang === "fr"
      ? `${title} vérifiés et structurés par RSTI — opportunités, projets et partenaires entre l'Afrique et le monde.`
      : `${title} verified and structured by RSTI — opportunities, projects and partners between Africa and the world.`,
    lang,
  });

  return (
    <div>
      <section className="border-b border-tertiary bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">RSTI</span>
          <h1 className="mt-3 font-serif text-4xl tracking-tight text-anthracite sm:text-5xl">{title}</h1>
          <div className="mt-8 max-w-2xl">
            <SearchBar suggestions={[]} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="mb-8 flex flex-wrap gap-2" data-testid="intent-filter">
          {[
            { v: "", l: t("all") },
            { v: "offer", l: t("intent_offer") },
            { v: "seek", l: t("intent_seek") },
            { v: "both", l: t("intent_both") },
          ].map((f) => (
            <button
              key={f.v}
              data-testid={`filter-intent-${f.v || "all"}`}
              onClick={() => setIntent(f.v)}
              className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                intent === f.v ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/70 hover:border-champagne"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="py-16 text-center text-anthracite/50">…</p>
        ) : listings.length === 0 ? (
          <div className="border border-dashed border-tertiary bg-white py-20 text-center" data-testid="category-empty">
            <p className="text-anthracite/50">
              {lang === "fr"
                ? "Aucune fiche publiée dans cette catégorie pour l'instant."
                : "No published entries in this category yet."}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {listings.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
